import { createScopeName } from '@ringcentral/mfe-transport';
export { GlobalTransport, createScopeName, getGlobalTransport, globalTransport } from '@ringcentral/mfe-transport';
import { global, identifierContainer, identifier, isSatisfied, satisfiesVersion, getEntryFromRegistry, getGlobal, fetchWithJsonp, injectScript, identifierAttribute, customElementName } from '@ringcentral/mfe-shared';
export { customElementName, identifierAttribute, satisfiesVersion } from '@ringcentral/mfe-shared';

const isSPAMode = () => !global[identifierContainer];
const getMeta = (name) => {
    if (isSPAMode()) {
        return null;
    }
    global[identifierContainer].entry = global.location.href;
    const { main, registry, registryType, registryAutoFetch, version, entry, modules, renderContainers, } = global[identifierContainer];
    return {
        name,
        data: {
            main,
            registry,
            registryType,
            registryAutoFetch,
            version,
            entry,
            modules,
            renderContainers,
        },
        loaded: Object.keys(modules),
        rendered: Object.keys(renderContainers),
    };
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const loadApp = (load, name) => {
    if (process.env.NODE_ENV !== 'production' && typeof name !== 'string') {
        throw new Error(`[MFE] 'name' should be a string.`);
    }
    global.__RC_MFE_USE_LOADER__ = true;
    return load(name);
};

const uuid = (name) => `rc-mfe:${name}-${Math.random().toString(36).slice(2, -1)}`;

const parserRemoteUrl = (remoteUrl) => {
    const link = new URL(remoteUrl.replace(/^.*@/, ''));
    const path = link.pathname.split('/').slice(0, -1).join('/');
    return Object.assign(Object.assign({}, link), { path: `${link.origin}${path}` });
};

/* eslint-disable consistent-return */
/* eslint-disable guard-for-in */
// TODO consider if dependency is reference by many module
const getConfigByDependency = (name) => {
    const { modules } = global[identifierContainer];
    for (const key in modules) {
        const config = modules[key];
        if (config.dependencies[name]) {
            return config;
        }
    }
    if (process.env.NODE_ENV !== 'production') {
        throw new Error(`[MFE] '${name}' is not a valid dependency.`);
    }
};
const getConfig = (name) => {
    const config = getConfigByDependency(name);
    if (process.env.NODE_ENV !== 'production' && !config) {
        throw new Error(`[MFE] ${name} config is not defined, make sure to use 'ModuleFederationPlugin' in '@ringcentral/mfe-builder' and set valid config for '@ringcentral/mfe-builder'.`);
    }
    return {
        dependencies: config.dependencies,
        name: config.name,
        registry: global[identifierContainer].registry,
    };
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var _a, _b, _c;
global[identifierContainer] = (_a = global[identifierContainer]) !== null && _a !== void 0 ? _a : {};
// Due to the fact that remoteEntry.js is loaded first and the remote registry may respond very quickly,
// it can lead to lost events, so a global cache has to be used here.
if (global[identifierContainer]._toBeResolvedUpdateStorage) {
    const { _toBeResolvedUpdateStorage } = global[identifierContainer];
    global[identifierContainer]._toBeResolvedUpdateStorage = null;
    _toBeResolvedUpdateStorage === null || _toBeResolvedUpdateStorage === void 0 ? void 0 : _toBeResolvedUpdateStorage.forEach((cb) => cb());
}
global[identifierContainer]._onUpdateStorage =
    (_b = global[identifierContainer]._onUpdateStorage) !== null && _b !== void 0 ? _b : new Set();
// when the remote update fetch has been completed, the update event will be cached for the next `onUpdateEntry(callback)` call
global[identifierContainer]._updateStorage =
    (_c = global[identifierContainer]._updateStorage) !== null && _c !== void 0 ? _c : new Set();
/**
 * Subscribe to the entry update event
 */
const onUpdateEntry = (callback) => {
    global[identifierContainer]._onUpdateStorage.add(callback);
    // trigger the update event when the remote update fetch has been completed
    global[identifierContainer]._updateStorage.forEach((data) => {
        callback(...data);
    });
    return () => {
        global[identifierContainer]._onUpdateStorage.delete(callback);
    };
};
const getStorageKey = (name) => {
    const { prefix, version: mainVersion, main } = global[identifierContainer];
    const storageKey = [identifier, main, mainVersion, prefix, name].join(':');
    return storageKey;
};
/**
 * Update the storage entry
 */
const updateStorageEntry = (
/**
 * The name of MFE that has been updated
 */
name, 
/**
 * The new MFE entry
 */
data) => {
    const storageKey = getStorageKey(name);
    return global[identifierContainer].storage.setItem(storageKey, JSON.stringify(data));
};
/**
 * Remove the storage entry
 */
const removeStorageEntry = (name) => {
    const storageKey = getStorageKey(name);
    return global[identifierContainer].storage.removeItem(storageKey);
};
const getStorageEntry = (name, dependencyVersion) => __awaiter(void 0, void 0, void 0, function* () {
    const storageKey = getStorageKey(name);
    try {
        const result = yield global[identifierContainer].storage.getItem(storageKey);
        if (!result) {
            return null;
        }
        const registryData = JSON.parse(result);
        if (dependencyVersion) {
            return isSatisfied(satisfiesVersion, registryData, dependencyVersion)
                ? registryData
                : null;
        }
        return registryData;
    }
    catch (error) {
        return null;
    }
});
/**
 * set the storage key for dynamic site config
 */
const setStorageMode = (name, value) => {
    const storageModeKey = [identifier, name].join(':');
    localStorage.setItem(storageModeKey, value);
};
const getStorageRegistryKey = (name) => [identifier, name, 'registry'].join(':');
/**
 * set the storage Registry for dynamic site config
 */
const setStorageRegistry = (name, value) => {
    const storageKey = getStorageRegistryKey(name);
    localStorage.setItem(storageKey, value);
};
/**
 * remove the storage Registry for dynamic site config
 */
const removeStorageRegistry = (name) => {
    const storageKey = getStorageRegistryKey(name);
    localStorage.removeItem(storageKey);
};

const insertStyle = (target, name) => {
    var _a, _b, _c;
    const { styles } = global[identifierContainer];
    styles[name] = (_a = styles[name]) !== null && _a !== void 0 ? _a : {};
    const insertedStyle = styles[name];
    (_b = insertedStyle.elements) !== null && _b !== void 0 ? _b : (insertedStyle.elements = []);
    (_c = insertedStyle.targets) !== null && _c !== void 0 ? _c : (insertedStyle.targets = []);
    // collections from `style-loader` insert CSS element
    // If using a style dynamic solution, such as `styled-components`, and be sure to use `StyleSheetManager`.
    if (!insertedStyle.targets.includes(target)) {
        insertedStyle.targets.push(target);
    }
    const inserted = {};
    insertedStyle.elements.forEach((style) => {
        if (style.tagName === 'LINK') {
            try {
                // Remove some of the styles injected in advance, such as `mini-css-extract-plugin`/`extract-css-chunks-webpack-plugin`.
                document.head.removeChild(style);
            }
            catch (e) {
                console.warn(e);
            }
        }
        const cssKey = style.href || style.innerText;
        if (!inserted[cssKey]) {
            inserted[cssKey] = true;
            target.appendChild(style.cloneNode(true));
        }
    });
};

const onWillMount = ({ target, name, id, }) => {
    if (isSPAMode() || !global[identifierContainer].styles)
        return;
    insertStyle(target, name);
};

const onMounted = ({ target, name, id, type, }) => {
    var _a, _b;
    if (isSPAMode() || !global[identifierContainer].styles)
        return;
    const { styles, renderContainers } = global[identifierContainer];
    styles[name] = (_a = styles[name]) !== null && _a !== void 0 ? _a : {};
    const insertedStyle = styles[name];
    (_b = insertedStyle.targets) === null || _b === void 0 ? void 0 : _b.splice(insertedStyle.targets.findIndex((insertedTarget) => insertedTarget === target), 1);
    renderContainers[id] = {
        name,
        type,
        timestamp: Date.now(),
    };
};

const onUnmount = ({ target, name, id, }) => {
    if (isSPAMode() || !global[identifierContainer].renderContainers)
        return;
    delete global[identifierContainer].renderContainers[id];
};

const expose = (options) => {
    if (!global.__RC_MFE_USE_LOADER__) {
        // auto init and render app as app shell
        const { render, init } = options;
        const result = init === null || init === void 0 ? void 0 : init();
        if (result instanceof Promise) {
            result.then(() => {
                render();
            });
        }
        else {
            render();
        }
    }
    return options;
};

const getEntry = (dependency, { autoFetch, autoStore = true, } = {}) => __awaiter(void 0, void 0, void 0, function* () {
    const { dependencies, name, version } = getConfigByDependency(dependency);
    const { entry: defaultEntry } = dependencies[dependency];
    const { registry, registryAutoFetch } = global[identifierContainer];
    if (registry) {
        return getEntryFromRegistry(getGlobal, fetchWithJsonp, satisfiesVersion, isSatisfied, {
            dependency,
            name,
            version,
            dependencyVersion: dependencies[dependency].dependencyVersion,
            defaultEntry,
            identifier,
            identifierContainer,
            autoFetch: autoFetch !== null && autoFetch !== void 0 ? autoFetch : registryAutoFetch,
            autoStore,
        });
    }
    return { entry: defaultEntry };
});

// container for module federation dynamic import
const getContainer = (name) => window[name];
const getModule = ({ name, module, }) => __awaiter(void 0, void 0, void 0, function* () {
    // Initializes the share scope. This fills it with known provided modules from this build and all remotes
    yield __webpack_init_sharing__('default');
    const container = getContainer(name); // or get the container somewhere else
    // Initialize the container, it may provide shared modules
    yield container.init(__webpack_share_scopes__.default);
    const factory = yield container.get(module);
    return factory();
});
/**
 * Dynamic load the module.
 */
const dynamicLoad = (
/**
 * The name of the module
 */
path, 
/**
 * The remote entry of the module
 */
entry, 
/**
 * The default remote of the module
 */
options = {}) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    global.__RC_MFE_USE_LOADER__ = true;
    const isContainsOrg = /^@/.test(path);
    const paths = path.split('/');
    const relativePath = isContainsOrg
        ? paths.slice(2).join('/')
        : paths.slice(1).join('/');
    const name = isContainsOrg ? paths.slice(0, 2).join('/') : paths[0];
    const modulePath = `./${relativePath}`;
    let link = entry;
    try {
        const container = getContainer(name);
        if (typeof container === 'undefined') {
            if (!link) {
                link = (yield getEntry(name)).entry;
            }
            yield injectScript({
                url: link,
                retryTimes: (_a = options.maxRetries) !== null && _a !== void 0 ? _a : 1,
                retryDelay: (_b = options.retryDelay) !== null && _b !== void 0 ? _b : 1000,
                injectScript,
            });
        }
        const module = yield getModule({
            name,
            module: modulePath,
        });
        if (typeof (module === null || module === void 0 ? void 0 : module.default) === 'undefined') {
            throw new Error(`[MFE] '${name}' app should export default exposed API.`);
        }
        return module;
    }
    catch (e) {
        console.error(`[MFE] Failed to import module ${modulePath} of ${name} app`);
        throw e;
    }
});

/* eslint-disable guard-for-in */
/**
 * Mount an app to a target element.
 */
const useApp = ({ name, loader, props, target, attrs, bootstrap, }) => {
    return loadApp(loader, name).then((module) => {
        if (typeof (module === null || module === void 0 ? void 0 : module.default) === 'undefined') {
            throw new Error(`[MFE] '${name}' app should export default exposed API.`);
        }
        const rootNode = document.createElement('div');
        const id = uuid(name);
        rootNode.setAttribute(identifierAttribute, id);
        const attributes = attrs !== null && attrs !== void 0 ? attrs : {};
        for (const key in attributes) {
            rootNode.setAttribute(key, attributes[key]);
        }
        target.appendChild(rootNode);
        const renderNode = document.createElement('div');
        rootNode.appendChild(renderNode);
        const { render, init } = module.default;
        const result = bootstrap ? bootstrap(module.default) : init === null || init === void 0 ? void 0 : init();
        const _render = () => {
            onWillMount({ target: rootNode, name, id });
            const callback = render(renderNode, props !== null && props !== void 0 ? props : {}, id);
            onMounted({ target: rootNode, name, id, type: "core-useApp" /* RenderType.App */ });
            return () => {
                onUnmount({ target: rootNode, name, id });
                callback === null || callback === void 0 ? void 0 : callback();
            };
        };
        if (result instanceof Promise) {
            return result.then(_render);
        }
        return _render();
    });
};

const getIframeUrl = (siteName) => __awaiter(void 0, void 0, void 0, function* () {
    const { dependencies, name, registry } = getConfig(siteName);
    if (typeof dependencies[siteName] === 'undefined') {
        throw new Error(`[MFE] The site named "${siteName}" is not in the dependency list of "${name}" app.`);
    }
    let rawUrl = dependencies[siteName].entry;
    if (registry) {
        rawUrl = (yield getEntry(siteName)).entry;
    }
    // TODO: support more flexible url config and custom iframe url in mfe dependencies config
    return parserRemoteUrl(rawUrl).path;
});
/**
 * Mount an app with iframe to a target element.
 */
const useIframe = ({ target, name, url, attrs, scope, }) => __awaiter(void 0, void 0, void 0, function* () {
    const iframe = document.createElement('iframe');
    iframe.src = url !== null && url !== void 0 ? url : (yield getIframeUrl(name));
    const id = uuid(name);
    iframe.setAttribute('frameBorder', 'no');
    const attributes = attrs !== null && attrs !== void 0 ? attrs : {};
    for (const key in attributes) {
        iframe.setAttribute(key, attributes[key]);
    }
    iframe.setAttribute(identifierAttribute, id);
    iframe.setAttribute('name', createScopeName(id, scope));
    target.appendChild(iframe);
    onMounted({ target, name, id, type: "core-useIframe" /* RenderType.Iframe */ });
    return () => {
        onUnmount({ target, name, id });
    };
});

/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
/* eslint-disable no-new-func */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable guard-for-in */
const mfeHTMLElement = `class RC_MFE extends HTMLElement {
  constructor() {
    super();
    if (!window.__RC_MFE_CUSTOM_ELEMENTS_INSTANCES__.has(this)) {
      window.__RC_MFE_CUSTOM_ELEMENTS_INSTANCES__.set(this);
    }
  }

  disconnectedCallback() {
    const fn = window.__RC_MFE_CUSTOM_ELEMENTS_INSTANCES__.get(this);
    if (fn) fn();
    window.__RC_MFE_CUSTOM_ELEMENTS_INSTANCES__.delete(this);
  }
}`;
// https://github.com/webcomponents/polyfills/tree/master/packages/webcomponentsjs#custom-elements-es5-adapterjs
global.__RC_MFE_CUSTOM_ELEMENTS_INSTANCES__ = new Map();
const defineCustomElement = (options) => {
    if (!customElements.get(customElementName)) {
        customElements.define(customElementName, Function(`return ${mfeHTMLElement}`)());
    }
    const renderNode = document.createElement('div');
    const customElementInstance = options.customElement;
    let injectedRoot;
    if (options.useShadowDOM && isSPAMode()) {
        if (process.env.NODE_ENV !== 'production') {
            console.error('[MFE] Shadow DOM is not supported in SPA mode, fallback to use light DOM');
        }
        options.useShadowDOM = false;
    }
    if (options.useShadowDOM &&
        !isSPAMode() &&
        !global[identifierContainer].enableCssIsolation) {
        if (process.env.NODE_ENV !== 'production') {
            console.error(`[MFE] Shadow DOM is not supported in MFE mode, fallback to use light DOM, and please use 'insertStyle' in '@ringcentral/mfe-builder' for webpack.`);
        }
        options.useShadowDOM = false;
    }
    if (options.useShadowDOM) {
        const shadow = customElementInstance.attachShadow({
            mode: options.shadowMode,
        });
        shadow.appendChild(renderNode);
        injectedRoot = shadow;
    }
    else {
        customElementInstance.appendChild(renderNode);
        injectedRoot = customElementInstance;
    }
    return { renderNode, injectedRoot: injectedRoot };
};
/**
 * Mount an app with WebComponents to a target element.
 */
const useWebComponents = ({ name, loader, target, shadowMode = 'open', useShadowDOM, attrs, props, bootstrap, }) => {
    return loadApp(loader, name).then((module) => {
        if (typeof (module === null || module === void 0 ? void 0 : module.default) === 'undefined') {
            throw new Error(`'[MFE] ${name}' app should export default exposed API.`);
        }
        const customElement = document.createElement(customElementName);
        const id = uuid(name);
        customElement.setAttribute(identifierAttribute, id);
        target.appendChild(customElement);
        const { renderNode, injectedRoot } = defineCustomElement({
            name,
            shadowMode,
            useShadowDOM,
            customElement,
        });
        const attributes = attrs !== null && attrs !== void 0 ? attrs : {};
        for (const key in attributes) {
            renderNode.setAttribute(key, attributes[key]);
        }
        const { render, init } = module.default;
        const result = bootstrap ? bootstrap(module.default) : init === null || init === void 0 ? void 0 : init();
        const _render = () => {
            onWillMount({ target: injectedRoot, name, id });
            const callback = render(renderNode, props !== null && props !== void 0 ? props : {}, id);
            onMounted({
                target: injectedRoot,
                name,
                id,
                type: "core-useWebComponents" /* RenderType.WebComponents */,
            });
            global.__RC_MFE_CUSTOM_ELEMENTS_INSTANCES__.set(customElement, () => {
                onUnmount({ target: injectedRoot, name, id });
                callback === null || callback === void 0 ? void 0 : callback();
            });
        };
        if (result instanceof Promise) {
            return result.then(_render);
        }
        return _render();
    });
};

export { defineCustomElement, dynamicLoad, expose, getEntry, getIframeUrl, getMeta, getStorageEntry, getStorageKey, loadApp, onMounted, onUnmount, onUpdateEntry, onWillMount, removeStorageEntry, removeStorageRegistry, setStorageMode, setStorageRegistry, updateStorageEntry, useApp, useIframe, useWebComponents, uuid };
//# sourceMappingURL=index.mjs.map
